import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import { logout } from "../../util/authentication";
import { getEmployeeStation, getEmployeeStationList, sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import UnauthorizedMessage from "../../components/UnauthorizedMessage";
import PageLoading from "../../components/PageLoading";
import { STATION_FILTER_MUST_BE_SELECTED } from "../../util/messageConstants";
import { BREADCRUMBS_TITLE, HOME_PATH, IAM_CONTRACT, STATION, TWU_CONTRACT } from "../../util/constants";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import { Grid, InputAdornment, TextField } from "@mui/material";
import StarsSelectInput from "../../components/fundamentals/StarsSelectInput";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../../slices/userSession/userSessionSlice";
import { useDispatch, useSelector } from "react-redux";
import { getUnionContractGivenWorkgroup, getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import StarsDatePicker from "../../components/fundamentals/StarsDatePicker";
import CancelIconButton from "../../components/fundamentals/CancelIconButton";
import SearchButton from "../../components/fundamentals/SearchButton";
import PropTypes from "prop-types";
import {
  selectEmployeeVoluntaryOTData,
  updateSearchString,
  updateSelectedWorkgroup,
  updateSelectedDate,
  updateAvailableWorkgroups,
} from "../../slices/employeeOvertime/employeeVoluntaryOTSlice";
import { FOURTEEN_DAYS_IN_FUTURE, TODAY, YESTERDAY } from "../../util/dateHelper";
import { convert_Luxon_To_DateFns, deserializeDateFromRedux, serializeDateToRedux } from "../../util/dateTimeFormatter";
import CreateVoluntaryOTSignupIAM from "../../components/overtime/CreateVoluntaryOTSignupIAM";
import CreateVoluntaryOTSignupTWU from "../../components/overtime/CreateVoluntaryOTSignupTWU";
import VoluntaryOTListMobile from "./VoluntaryOTListMobile";
import VoluntaryOTListMobileFilterDialog from "../../components/overtime/VoluntaryOTListMobileFilterDialog";
import { OPS_CARGO_AGENTS_WORKGROUP_LIST } from "../../util/overtime/constants";
import VoluntaryOTListTable from "../../components/overtime/VoluntaryOTListTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { getVoluntaryOTList } from "../../api/overtime";
import { overtimeDataErrorCodeToErrorMessageMap } from "../../util/errorMapper";
import MoreInfoDialog from "../../components/overtime/MoreInfoDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    width: "100%",
  },
  pageTitle: {
    marginLeft: "2rem",
    fontSize: "24px",
    maxWidth: "90vw",
    textAlign: "left",
    marginTop: "10px",
  },
  inputRow: {
    width: "100%",
    display: "flex",
  },
  inputStation: {
    width: "5.2rem",
    marginRight: "1rem",
  },
  inputSelect: {
    width: "13rem",
    textAlign: "left",
  },
  inputSelectSection: {
    marginRight: "1rem",
  },
  inputSelectMobile: {
    width: "47vw",
    left: "5px",
  },
  inputSelectSearchMobile: {
    width: "47vw",
    right: "5px",
  },
  filterDialogInputMobile: {
    width: "100%",
    textAlign: "left",
  },
  buttonsSection: {
    width: "100%",
    alignContent: "flex-end",
  },
  tableSection: {
    width: "100%",
    marginTop: "1rem",
  },
}));

const VoluntaryOTList = (props) => {
  //#region Constants
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const employeeVoluntaryOTData = useSelector(selectEmployeeVoluntaryOTData);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  //const [isSearch, setIsSearch] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [station, setStation] = useState("");
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.VOLUNTARY_OT_LIST];
  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.path];
  const [otRequests, setOtRequests] = useState([]);
  const otTableDesktopRef = useGridApiRef();
  const [otRequestSelectedForMoreInfo, setOtRequestSelectedForMoreInfo] = useState(null);
  const [isMobileFilterDialogOpen, setIsMobileFilterDialogOpen] = useState(false);
  //#endregion

  //#region Hooks
  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      // If employee's workgroup is Ops or Cargo Agent, set both as available workgroups as they can sign up for OT in either
      const workgroup = getWorkgroupGivenJobCode(userSession.jobCode);
      if (OPS_CARGO_AGENTS_WORKGROUP_LIST.includes(workgroup)) {
        dispatch(updateAvailableWorkgroups(OPS_CARGO_AGENTS_WORKGROUP_LIST));
      } else {
        dispatch(updateAvailableWorkgroups([workgroup]));
      }
      if (!employeeVoluntaryOTData.selectedWorkgroup) dispatch(updateSelectedWorkgroup(workgroup));

      const station = getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter);
      setStation(station);

      if (!employeeVoluntaryOTData.selectedDate) dispatch(updateSelectedDate(serializeDateToRedux(TODAY(station))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    fetchVoluntaryOTList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeVoluntaryOTData.selectedWorkgroup, station, employeeVoluntaryOTData.selectedDate]);
  //#endregion

  //#region API Calls
  const fetchVoluntaryOTList = async () => {
    try {
      const selectedDate = getSelectedDateSafely();
      if (station && employeeVoluntaryOTData.selectedWorkgroup && selectedDate) {
        setIsLoading(true);
        setIsError(false);
        setErrorObject({});
        const date = selectedDate.toISO().split("T")[0];
        const response = await getVoluntaryOTList(date, station, employeeVoluntaryOTData.selectedWorkgroup);
        if (response.ok) {
          const votList = await response.json();
          setOtRequests(votList);
        } else {
          setIsError(true);
          setErrorObject(overtimeDataErrorCodeToErrorMessageMap.unexpectedOvertimeDataError);
        }
      }
    } catch (error) {
      console.error("Unexpected error during fetchVoluntaryOTList: ", error);
      setIsError(true);
      setErrorObject(overtimeDataErrorCodeToErrorMessageMap.unexpectedOvertimeDataError);
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion

  //#region Input Validations
  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  //   const applySearch = (searchString, searchedTimeOffRequests) => {
  //     if (searchString === "") {
  //       //setIsSearch(false);
  //       //   setSearchedTimeOffRequests(searchedTimeOffRequests);
  //       applyTableScroll();
  //       //setIsLoading(false);
  //     } else {
  //       //setIsSearch(true);
  //       //   setSearchedTimeOffRequests(searchedTimeOffRequests);
  //       applyTableScroll();
  //       //setIsLoading(false);
  //     }
  //   };

  //   const applyTableScroll = () => {
  //     const currentDesktop = votTableDesktopRef.current;
  //     if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
  //       currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
  //     }
  //   };

  const getSelectedDateSafely = () => {
    // This safe function is used to be sure the default value is provided in the event the redux state is not available yet
    return employeeVoluntaryOTData.selectedDate ? deserializeDateFromRedux(employeeVoluntaryOTData.selectedDate) : null;
  };
  //#endregion

  //#region Event Handlers
  const handleStationFilterChange = (event) => {
    setStation(event.target.value);
    dispatch(updateStationFilter(event.target.value));
  };

  const handleWorkgroupInputChange = (event) => {
    dispatch(updateSelectedWorkgroup(event.target.value));
    //dispatch(updateSelectedWopType(""));
    // setTimeOffRequests([]);
    // setSearchedTimeOffRequests([]);
  };

  const handleDateChange = (date) => {
    dispatch(updateSelectedDate(serializeDateToRedux(date)));
    // setTimeOffRequests([]);
    // setSearchedTimeOffRequests([]);
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(employeeVoluntaryOTData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    // const selectedDate = getSelectedDateSafely();
    // if (station && employeeVoluntaryOTData.selectedWorkgroup && selectedDate) {
    //   dispatch(updateSearchStringErrors(""));
    //   const newSearchStringTrimmed = newSearchString.trim();
    //   if (
    //     !isNaN(parseInt(newSearchStringTrimmed)) &&
    //     parseInt(newSearchStringTrimmed) >= 10000000 &&
    //     parseInt(newSearchStringTrimmed) <= 99999999
    //   ) {
    //     dispatch(updateEmployeeVoluntaryOTFoundByReferenceNumberSearchIsLoading(true));
    // fetchTimeOffRequestSnapshotByReferenceNumber(newSearchStringTrimmed, userSession.eid, station, isAdminPage)
    //   .then((result) => {
    //     if (result.message) {
    //       console.error("Error fetching time off request snapshot by reference number:", result.message);
    //       dispatch(updateSearchStringErrors(result.message));
    //       dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
    //     } else {
    //       let timeOffRequestSnapshotResult = result.torSnapshotObject;
    //       let scrubbedTimeOffRequestSnapshot = getScrubbedTimeOffRequestSnapshot(timeOffRequestSnapshotResult);
    //       if (scrubbedTimeOffRequestSnapshot.timeOffRequestType === employeeTimeOffRequestWOPData.selectedWopType) {
    //         dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearch(timeOffRequestSnapshotResult));
    //         dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
    //         applySearch(newSearchStringTrimmed, [scrubbedTimeOffRequestSnapshot]);
    //         dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
    //       } else {
    //         dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
    //         dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
    //       }
    //     }
    //   })
    //   .catch((err) => {
    //     console.error("error happened!", err);
    //     dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
    //     dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(false));
    //   });
    //   } else {
    //     // const torsToSearch = isStatusSelectAllChecked() ? timeOffRequests : filteredTimeOffRequests;
    //     // initializeWOPSearchIndex(torsToSearch, isAdminPage);
    //     // searchTimeOffRequestsWOP(newSearchStringTrimmed, applySearch);
    //   }
    // }
  };

  const handleClickMobileFilterDialogOpen = () => {
    setIsMobileFilterDialogOpen(true);
  };

  const handleClickMobileFilterDialogClose = () => {
    setIsMobileFilterDialogOpen(false);
  };

  const handleClickMobileFilterDialogApplyFiltersButton = () => {
    setIsMobileFilterDialogOpen(false);
  };

  const handleMoreInfoDialogClose = () => {
    setOtRequestSelectedForMoreInfo(null);
  };
  //#endregion

  //#region Component Renders
  const renderScreenTitle = () => {
    if (getUnionContractGivenWorkgroup(employeeVoluntaryOTData.selectedWorkgroup) === IAM_CONTRACT) {
      return "Customer Service Standard Overtime Callbook";
    } else if (getUnionContractGivenWorkgroup(employeeVoluntaryOTData.selectedWorkgroup) === TWU_CONTRACT) {
      return "Southwest Airlines Ramp, Operations, Air Freight and Provisioning Standard Overtime Call Book";
    }
    return "";
  };

  const renderStationInput = () => {
    return (
      <StarsSelectInput
        label={STATION}
        menuMessage={STATION}
        value={stationFilter}
        allOptions={getEmployeeStationList(userSession)}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={(event) => handleStationFilterChange(event)}
        errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
        className={props.isDesktopView ? classes.inputStation : classes.filterDialogInputMobile}
        isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
        disabled={getEmployeeStationList(userSession).length <= 1}
        fullWidth={isMobileFilterDialogOpen}
      />
    );
  };

  const renderWorkgroupInput = () => {
    return (
      <StarsSelectInput
        label="Workgroup"
        menuMessage="Workgroup"
        value={employeeVoluntaryOTData.selectedWorkgroup}
        allOptions={employeeVoluntaryOTData.availableWorkgroups}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={(event) => handleWorkgroupInputChange(event)}
        className={props.isDesktopView ? classes.inputSelect : classes.filterDialogInputMobile}
        disabled={employeeVoluntaryOTData.availableWorkgroups.length <= 1}
        errors=""
        fullWidth={isMobileFilterDialogOpen}
      />
    );
  };

  const renderDateInput = () => {
    return (
      <StarsDatePicker
        label="Date"
        date={getSelectedDateSafely()}
        onChange={handleDateChange}
        dateErrors=""
        className={props.isDesktopView ? classes.inputSelect : classes.inputSelectMobile}
        minDate={convert_Luxon_To_DateFns(YESTERDAY(station))}
        maxDate={convert_Luxon_To_DateFns(FOURTEEN_DAYS_IN_FUTURE(station))}
      />
    );
  };

  const renderSearchInput = () => {
    return (
      <TextField
        label="Search"
        variant="outlined"
        value={employeeVoluntaryOTData.searchString}
        onChange={handleSearchChange.bind(this)}
        onBlur={handleSearchBlur.bind(this)}
        onKeyUp={handleSearchEnterKeyPressed.bind(this)}
        className={props.isDesktopView ? classes.inputSelect : classes.inputSelectSearchMobile}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {employeeVoluntaryOTData.searchString !== "" ? (
                <CancelIconButton onSearchCancel={handleSearchCancel} />
              ) : (
                ""
              )}
              <SearchButton onSearchSubmit={handleSearchSubmit} searchString={employeeVoluntaryOTData.searchString} />
            </InputAdornment>
          ),
        }}
        error={employeeVoluntaryOTData.searchStringErrors !== ""}
        helperText={employeeVoluntaryOTData.searchStringErrors}
      />
    );
  };

  const renderDesktopVoluntaryOTList = () => {
    return (
      <div className={classes.root}>
        <Grid>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLinks}
            breadcrumbsList={breadcrumbsList}
          />
        </Grid>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item container justifyContent="flex-start" md={8} lg={8}>
            <Grid container className={classes.pageTitle}>
              {renderScreenTitle()}
            </Grid>
            <br />
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            pt: 3,
            display: "flex",
            justifyContent: "start",
            paddingTop: "10px",
            paddingLeft: "3rem",
            paddingRight: "3rem",
            paddingBottom: "10px",
          }}
          spacing={2}
        >
          <div className={classes.inputRow}>
            <div>{renderStationInput()}</div>
            <div className={classes.inputSelectSection}>{renderWorkgroupInput()}</div>
            <div className={classes.inputSelectSection}>{renderDateInput()}</div>
            <div className={classes.inputSelectSection}>{renderSearchInput()}</div>
            <div className={classes.buttonsSection}>
              {getUnionContractGivenWorkgroup(employeeVoluntaryOTData.selectedWorkgroup) === IAM_CONTRACT && (
                <CreateVoluntaryOTSignupIAM
                  isDesktopView={props.isDesktopView}
                  station={station}
                  workgroup={employeeVoluntaryOTData.selectedWorkgroup}
                  selectedDate={getSelectedDateSafely()}
                />
              )}
              {getUnionContractGivenWorkgroup(employeeVoluntaryOTData.selectedWorkgroup) === TWU_CONTRACT && (
                <CreateVoluntaryOTSignupTWU
                  isDesktopView={props.isDesktopView}
                  station={station}
                  workgroup={employeeVoluntaryOTData.selectedWorkgroup}
                  selectedDate={getSelectedDateSafely()}
                />
              )}
            </div>
          </div>
          {getUnionContractGivenWorkgroup(employeeVoluntaryOTData.selectedWorkgroup) === IAM_CONTRACT && (
            <div className={classes.tableSection}>
              <VoluntaryOTListTable
                otRequests={
                  //isSearch
                  //? [] searchedTimeOffRequests
                  otRequests
                }
                isLoading={isLoading}
                isError={isError}
                errorObject={errorObject}
                otTableDesktopRef={otTableDesktopRef}
                isDesktopView={props.isDesktopView}
                setOtRequestSelectedForMoreInfo={setOtRequestSelectedForMoreInfo}
              />
            </div>
          )}
        </Grid>
        {otRequestSelectedForMoreInfo && (
          <MoreInfoDialog
            otRequest={otRequestSelectedForMoreInfo}
            handleMoreInfoDialogClose={handleMoreInfoDialogClose}
            isDesktopView={props.isDesktopView}
          />
        )}
      </div>
    );
  };

  const renderMobileVoluntaryOTList = () => {
    return (
      <div data-testid="voluntary-ot-list-mobile">
        <VoluntaryOTListMobile
          searchInput={renderSearchInput()}
          dateInput={renderDateInput()}
          selectedDate={getSelectedDateSafely()}
          station={station}
          workgroup={employeeVoluntaryOTData.selectedWorkgroup}
          handleClickMobileFilterDialogOpen={handleClickMobileFilterDialogOpen}
          isLoading={isLoading}
          isDesktopView={props.isDesktopView}
          renderScreenTitle={renderScreenTitle}
          otRequests={otRequests}
          setOtRequestSelectedForMoreInfo={setOtRequestSelectedForMoreInfo}
        />
        <VoluntaryOTListMobileFilterDialog
          stationSelect={renderStationInput()}
          workgroupSelect={renderWorkgroupInput()}
          station={station}
          selectedWorkgroup={employeeVoluntaryOTData.selectedWorkgroup}
          isMobileFilterDialogOpen={isMobileFilterDialogOpen}
          handleClickMobileFilterDialogClose={handleClickMobileFilterDialogClose}
          handleClickMobileFilterDialogApplyFiltersButton={handleClickMobileFilterDialogApplyFiltersButton}
        />
        {otRequestSelectedForMoreInfo && (
          <MoreInfoDialog
            otRequest={otRequestSelectedForMoreInfo}
            handleMoreInfoDialogClose={handleMoreInfoDialogClose}
            isDesktopView={props.isDesktopView}
          />
        )}
      </div>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="voluntary-ot-list">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopVoluntaryOTList() : renderMobileVoluntaryOTList()}</>
      )}
    </div>
  );
  //#endregion
};

VoluntaryOTList.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default VoluntaryOTList;
